import type { TemplateDoc } from '../template';
import { activeFx, pieceRanks, sceneTimeline, transitionDuration } from './compile';
import { layerScene } from './scenes';
import { docMotionSchema, type MotionTrack } from './schema';
import { SFX, sfxSrc, type SfxKind } from './sfx';
import { TRANSITION_MAP } from './transitions';

/**
 * Automatic sound design: effects placed by rules from the choreography, as ordinary `sfx`
 * tracks (they can be moved, trimmed or removed like any other sound).
 *
 * - 0.4 s before a transition → whoosh (its peak lands on the swap); a hard cut → hit;
 * - the last scene is announced by a riser;
 * - letters, words or lines landing → ticks; typewriter text and terminals → typing;
 * - pops, slams, glitches, a clicking cursor, countdowns and kinetic words get their own sound.
 *
 * Re-running replaces the previous automatic tracks (ids starting with `auto-`) and keeps the
 * user's own sounds.
 */

export const AUTO_SFX_PREFIX = 'auto-';
const MAX_TRACKS = 30;

interface Cue {
  kind: SfxKind;
  /** When the sound should land (its peak), ms. */
  at: number;
  /** Play only this long (typewriter), ms. */
  length?: number;
  /** Lower number = kept first when there are too many. */
  priority: number;
  volume?: number;
}

const LETTER_PRESETS = new Set([
  'letters-pop',
  'letters-drop',
  'letters-blur',
  'words-rise',
  'words-flip',
  'lines-mask',
]);
const POP_PRESETS = new Set(['pop', 'elastic', 'drop', 'letters-pop', 'unfold']);
const SLAM_PRESETS = new Set(['zoom-out', 'stretch', 'whip']);

function pieces(text: string, by: string): number {
  const words = text.split(/\s+/).filter(Boolean);
  if (by === 'word') return words.length;
  if (by === 'char') return words.reduce((a, w) => a + [...w].length, 0);
  if (by === 'line') return Math.max(1, text.split('\n').length);
  return 1;
}

function cues(doc: TemplateDoc): Cue[] {
  const motion = docMotionSchema.parse(doc.motion ?? {});
  const { scenes, duration } = sceneTimeline(motion);
  const byId = new Map(scenes.map((s) => [s.id, s]));
  const out: Cue[] = [];

  // Scene changes
  motion.scenes.forEach((s, i) => {
    if (i >= motion.scenes.length - 1) return;
    const td = transitionDuration(motion, i);
    const preset = s.transition ? TRANSITION_MAP.get(s.transition.preset) : undefined;
    const end = scenes[i].end;
    if (!preset || preset.id === 'cut' || td <= 0) out.push({ kind: 'hit', at: end, priority: 0 });
    else out.push({ kind: 'whoosh', at: end - td / 2, priority: 0 });
  });
  if (motion.scenes.length >= 3 && duration >= 6000) {
    const last = scenes[scenes.length - 1];
    out.push({ kind: 'riser', at: last.start + 80, priority: 2, volume: 0.35 });
  }

  // Layers landing
  for (const layer of doc.layers) {
    const m = layer.motion;
    if (!m?.enter || !layer.visible) continue;
    const sceneId = m.scene ?? layerScene(doc, layer);
    const from = (sceneId ? byId.get(sceneId)?.start : 0) ?? 0;
    const start = from + m.enter.delay;
    const end = start + m.enter.duration;
    const split = layer.type === 'text' && m.split && m.split.by !== 'none' ? m.split : null;
    if (m.enter.preset === 'typewriter' && layer.type === 'text') {
      const n = split ? pieces(layer.text, split.by) : [...layer.text].length;
      const span = m.enter.duration + (split ? split.stagger * (n - 1) : 0);
      out.push({
        kind: 'typewriter',
        at: start,
        length: Math.max(200, Math.min(span, SFX.typewriter.duration)),
        priority: 1,
      });
      continue;
    }
    if (split && LETTER_PRESETS.has(m.enter.preset) && layer.type === 'text') {
      const n = pieces(layer.text, split.by);
      const ranks = pieceRanks(n, split.order, 1);
      const maxRank = Math.max(0, ...ranks);
      // At most 6 ticks per layer, spread over the pieces
      const ticks = Math.min(6, maxRank + 1);
      for (let k = 0; k < ticks; k++) {
        const rank = ticks > 1 ? Math.round((maxRank * k) / (ticks - 1)) : 0;
        out.push({ kind: 'tick', at: end + rank * split.stagger, priority: 3 });
      }
      continue;
    }
    if (POP_PRESETS.has(m.enter.preset))
      out.push({ kind: 'pop', at: start + m.enter.duration * 0.25, priority: 3 });
    else if (SLAM_PRESETS.has(m.enter.preset))
      out.push({ kind: 'hit', at: end, priority: 2, volume: 0.5 });
    else if (m.enter.preset === 'glitch') out.push({ kind: 'glitch', at: start, priority: 3 });
  }

  // Motion graphics
  for (const fx of activeFx(doc)) {
    const p = fx.params ?? {};
    const D = fx.duration;
    switch (fx.kind) {
      case 'terminal':
      case 'code':
        out.push({
          kind: 'typewriter',
          at: fx.start + (fx.kind === 'code' ? 400 : 250),
          length: Math.min(D * 0.8, SFX.typewriter.duration),
          priority: 1,
        });
        break;
      case 'words': {
        const raw = typeof p.text === 'string' ? p.text : '';
        const n = raw.trim()
          ? Math.min(10, raw.split(/\n|\s\/\s/).filter((s) => s.trim()).length)
          : 3;
        for (let i = 0; i < n; i++)
          out.push({ kind: 'hit', at: fx.start + (D * i) / n, priority: 1, volume: 0.6 });
        break;
      }
      case 'countdown': {
        const n = Math.max(1, Math.min(10, Number(p.from) || 3));
        for (let i = 0; i < n; i++)
          out.push({ kind: 'tick', at: fx.start + (D * i) / n, priority: 2, volume: 0.6 });
        out.push({ kind: 'boom', at: fx.start + D, priority: 2 });
        break;
      }
      case 'cursor':
        out.push({ kind: 'click', at: fx.start + D * 0.62, priority: 2 });
        break;
      case 'glitch':
      case 'glitch-text':
      case 'slice-text':
        out.push({ kind: 'glitch', at: fx.start, priority: 2 });
        break;
      case 'title':
      case 'extrude':
        out.push({ kind: 'hit', at: fx.start + 150, priority: 2 });
        break;
      case 'warp':
        out.push({ kind: 'riser', at: fx.start + D, priority: 2 });
        break;
      case 'confetti':
      case 'burst':
      case 'sticker':
      case 'shape-morph':
        out.push({ kind: 'pop', at: fx.start + 60, priority: 3 });
        break;
      case 'sweep':
      case 'neon':
        out.push({ kind: 'shimmer', at: fx.start + 100, priority: 3 });
        break;
      case 'counter':
      case 'donut':
      case 'split-flap':
        out.push({
          kind: 'typewriter',
          at: fx.start,
          length: Math.min(900, D * 0.6),
          priority: 3,
          volume: 0.25,
        });
        break;
    }
  }
  return out.filter((c) => c.at >= 0 && c.at <= duration);
}

/** Effect tracks for a document by the rules above (not yet placed in it). */
export function autoSfxTracks(
  doc: TemplateDoc,
  opts: { volume?: number; keep?: number } = {},
): MotionTrack[] {
  const total = sceneTimeline(docMotionSchema.parse(doc.motion ?? {})).duration;
  const room = Math.max(0, MAX_TRACKS - (opts.keep ?? 0));
  const gain = Math.max(0, Math.min(1, opts.volume ?? 1));
  // Most important first; the same sound twice within 120 ms is one sound
  const list = cues(doc).sort((a, b) => a.priority - b.priority || a.at - b.at);
  const picked: Cue[] = [];
  for (const c of list) {
    if (picked.length >= room) break;
    if (picked.some((p) => p.kind === c.kind && Math.abs(p.at - c.at) < 120)) continue;
    picked.push(c);
  }
  picked.sort((a, b) => a.at - b.at);
  return picked
    .map((c, i): MotionTrack | null => {
      const info = SFX[c.kind];
      const start = Math.round(c.at - info.peak);
      const trimStart = start < 0 ? -start : 0;
      const at = Math.max(0, start);
      const length = Math.min(
        c.length ?? info.duration - trimStart,
        info.duration - trimStart,
        total - at,
      );
      if (length < 50) return null;
      return {
        id: `${AUTO_SFX_PREFIX}${c.kind}-${i + 1}`,
        kind: 'sfx',
        src: sfxSrc(c.kind),
        name: c.kind,
        duration: info.duration,
        start: at,
        trimStart: Math.round(trimStart),
        length: Math.round(length),
        volume: Math.round((c.volume ?? info.volume) * gain * 100) / 100,
        fadeIn: 0,
        fadeOut: c.kind === 'typewriter' ? 120 : 0,
      };
    })
    .filter((t): t is MotionTrack => t !== null);
}

/** The document with its automatic effects (re)placed; the user's own sounds stay. */
export function applyAutoSfx(doc: TemplateDoc, opts: { volume?: number } = {}): TemplateDoc {
  const motion = docMotionSchema.parse(doc.motion ?? {});
  const own = motion.tracks.filter((t) => !t.id.startsWith(AUTO_SFX_PREFIX));
  const auto = autoSfxTracks(doc, { ...opts, keep: own.length });
  return { ...doc, motion: { ...motion, tracks: [...own, ...auto] } };
}

/** Remove the automatic effects. */
export function clearAutoSfx(doc: TemplateDoc): TemplateDoc {
  if (!doc.motion) return doc;
  const motion = docMotionSchema.parse(doc.motion);
  return {
    ...doc,
    motion: { ...motion, tracks: motion.tracks.filter((t) => !t.id.startsWith(AUTO_SFX_PREFIX)) },
  };
}
